import { useState, useEffect } from 'react'
import { Loader2, AlertTriangle, CheckCircle, Calendar, ChevronDown, Clock, Scissors, Bookmark, Check } from 'lucide-react'
import { useCallStore, getMissingFields } from '../stores/callStore'
import { DatePicker } from './DatePicker'
import { TimePicker } from './TimePicker'
import { ServiceSelector } from './ServiceSelector'
import type { ProfileFields } from '../types'

interface ProfileCardProps {
  onBookAppointment?: () => void
  isBooking?: boolean
}

interface StylistInfo {
  id: string
  name: string
  role: string
  color_hex: string
  photo_url?: string
}

interface PriceInfo {
  defaultPrice: number
  finalPrice: number
  stylistId: string | null
  stylistName: string | null
}

// Stylist photo mapping
// Stylist photos - hosted locally in public/stylists/
const STYLIST_PHOTOS: Record<string, string> = {
  'Dave Baker': '/stylists/dave.jpg',
  'Ben Hendry': '/stylists/ben.jpg',
  'Sylwia Donczyk': '/stylists/sylwia.jpg',
  'Callum Thayne': '/stylists/callum.jpg',
  'Elvis': '/stylists/elvis.jpg',
  'Joanna Eden': '/stylists/joanna.jpg',
  'Lily': '/stylists/lily.jpg',
}

const API_URL = (import.meta.env.VITE_API_URL as string) || 'https://salonflow-api.empty-mud-1234.workers.dev'

// Format date for display (e.g., "Thu 12th Dec")
const formatDateDisplay = (dateStr: string | null | undefined): string => {
  if (!dateStr) return ''
  try {
    const date = new Date(dateStr)
    const day = date.toLocaleDateString('en-GB', { weekday: 'short' })
    const dayNum = date.getDate()
    const suffix = ['th', 'st', 'nd', 'rd'][(dayNum % 10 > 3 || Math.floor(dayNum / 10) === 1) ? 0 : dayNum % 10]
    const month = date.toLocaleDateString('en-GB', { month: 'short' })
    return `${day} ${dayNum}${suffix} ${month}`
  } catch {
    return dateStr
  }
}

// Format time for display (e.g., "2:00pm")
const formatTimeDisplay = (timeStr: string | null | undefined): string => {
  if (!timeStr) return ''
  try {
    const [hours, mins] = timeStr.split(':').map(Number)
    const h = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours
    const ampm = hours >= 12 ? 'pm' : 'am'
    return `${h}:${mins.toString().padStart(2, '0')}${ampm}`
  } catch {
    return timeStr
  }
}

// Compact field component
function CompactField({ 
  label, 
  value, 
  confidence,
  status,
  placeholder,
  onUpdate,
  fieldKey,
  className = ''
}: { 
  label: string
  value?: string | null
  confidence?: number
  status?: string
  placeholder: string
  onUpdate: (key: string, value: string) => void
  fieldKey: string
  className?: string
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value || '')

  useEffect(() => {
    setEditValue(value || '')
  }, [value])

  const handleSave = () => {
    onUpdate(fieldKey, editValue)
    setIsEditing(false)
  }

  const getStatusColor = () => {
    if (!value) return 'border-gray-200 bg-gray-50'
    if (status === 'confirmed' || (confidence && confidence >= 80)) return 'border-green-300 bg-green-50'
    if (status === 'warning' || (confidence && confidence < 50)) return 'border-amber-300 bg-amber-50'
    return 'border-blue-300 bg-blue-50'
  }

  return (
    <div className={`${className}`}>
      <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{label}</label>
      {isEditing ? (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          autoFocus
          className="w-full px-2 py-1.5 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      ) : (
        <div 
          onClick={() => setIsEditing(true)}
          className={`px-2 py-1.5 text-sm rounded border cursor-pointer hover:border-blue-400 transition-colors ${getStatusColor()}`}
        >
          {value || <span className="text-gray-400">{placeholder}</span>}
        </div>
      )}
    </div>
  )
}

// Interactive Picker Field (for Date, Time, Service)
function PickerField({ 
  label, 
  value, 
  displayValue,
  confidence,
  status,
  placeholder,
  icon: Icon,
  onClick,
  isOpen,
  className = ''
}: { 
  label: string
  value?: string | null
  displayValue?: string
  confidence?: number
  status?: string
  placeholder: string
  icon?: React.ElementType
  onClick: () => void
  isOpen: boolean
  className?: string
}) {
  const getStatusColor = () => {
    if (!value) return 'border-gray-200 bg-gray-50'
    if (status === 'confirmed' || (confidence && confidence >= 80)) return 'border-green-300 bg-green-50'
    if (status === 'warning' || (confidence && confidence < 50)) return 'border-amber-300 bg-amber-50'
    return 'border-blue-300 bg-blue-50'
  }

  return (
    <div className={`relative ${className}`}>
      <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{label}</label>
      <button
        onClick={onClick}
        className={`
          w-full px-2 py-1.5 text-sm rounded border cursor-pointer transition-all
          flex items-center justify-between gap-1
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-200' : 'hover:border-blue-400'}
          ${getStatusColor()}
        `}
      >
        <span className="flex items-center gap-1.5 truncate">
          {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
          {displayValue || value || <span className="text-gray-400">{placeholder}</span>}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
    </div>
  )
}

// Notes Section with Save to Profile functionality
function NotesSection({ 
  value, 
  confidence, 
  status, 
  onUpdate,
  customerPhone
}: {
  value?: string | null
  confidence?: number
  status?: string
  onUpdate: (value: string) => void
  customerPhone?: string | null
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value || '')
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [showCategoryPicker, setShowCategoryPicker] = useState(false)

  useEffect(() => {
    setEditValue(value || '')
  }, [value])

  const handleSave = () => {
    onUpdate(editValue)
    setIsEditing(false)
  }

  const handleSaveToProfile = async (category?: string) => {
    if (!customerPhone || !editValue.trim()) return
    
    setIsSaving(true)
    setShowCategoryPicker(false)
    
    try {
      const response = await fetch(`${API_URL}/api/customers/phone/${encodeURIComponent(customerPhone)}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          note: editValue.trim(),
          category: category || 'note'
        })
      })
      
      if (response.ok) {
        setSaveSuccess(true)
        setTimeout(() => setSaveSuccess(false), 2000)
      }
    } catch (err) {
      console.error('Failed to save note to profile:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const getStatusColor = () => {
    if (!value) return 'border-gray-200 bg-gray-50'
    if (status === 'confirmed' || (confidence && confidence >= 80)) return 'border-green-300 bg-green-50'
    if (status === 'warning' || (confidence && confidence < 50)) return 'border-amber-300 bg-amber-50'
    return 'border-blue-300 bg-blue-50'
  }

  const categories = [
    { id: 'personal', label: 'Personal', icon: '👤', desc: 'Birthday, family, preferences' },
    { id: 'hair', label: 'Hair', icon: '💇', desc: 'Style preferences, allergies' },
    { id: 'vip', label: 'VIP', icon: '⭐', desc: 'Special treatment notes' },
    { id: 'note', label: 'General', icon: '📝', desc: 'Other notes' },
  ]

  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Notes</label>
        {value && customerPhone && (
          <div className="relative">
            <button
              onClick={() => setShowCategoryPicker(!showCategoryPicker)}
              disabled={isSaving}
              className={`text-[10px] font-medium flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${
                saveSuccess 
                  ? 'text-green-600 bg-green-50' 
                  : 'text-blue-600 hover:bg-blue-50'
              }`}
            >
              {isSaving ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : saveSuccess ? (
                <>
                  <Check className="w-3 h-3" />
                  Saved!
                </>
              ) : (
                <>
                  <Bookmark className="w-3 h-3" />
                  Save to Profile
                </>
              )}
            </button>
            
            {/* Category Picker Dropdown */}
            {showCategoryPicker && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-50 w-48">
                <div className="text-[10px] text-gray-500 mb-2 px-1">Save as:</div>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => handleSaveToProfile(cat.id)}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 flex items-center gap-2"
                  >
                    <span>{cat.icon}</span>
                    <div>
                      <div className="text-xs font-medium text-gray-900">{cat.label}</div>
                      <div className="text-[10px] text-gray-500">{cat.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {isEditing ? (
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          autoFocus
          rows={3}
          className="w-full px-2 py-1.5 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
          placeholder="Any special requests, personal notes..."
        />
      ) : value ? (
        <div 
          onClick={() => setIsEditing(true)}
          className={`px-2 py-1.5 text-sm rounded border cursor-pointer hover:border-blue-400 transition-colors ${getStatusColor()}`}
        >
          {/* Render bullet points as separate items */}
          <div className="space-y-1">
            {value.split('\n').map((line, i) => {
              const cleanLine = line.replace(/^[•\-]\s*/, '').trim()
              if (!cleanLine) return null
              return (
                <div key={i} className="flex items-start gap-1.5">
                  <span className="text-blue-500 mt-0.5">•</span>
                  <span className="flex-1">{cleanLine}</span>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div 
          onClick={() => setIsEditing(true)}
          className={`px-2 py-1.5 text-sm rounded border cursor-pointer hover:border-blue-400 transition-colors min-h-[36px] ${getStatusColor()}`}
        >
          <span className="text-gray-400">Click to add notes...</span>
        </div>
      )}
    </div>
  )
}

export function ProfileCard({ onBookAppointment, isBooking = false }: ProfileCardProps) {
  const { 
    fields, 
    isBookingReady, 
    overallConfidence,
    isCallActive,
    currentSessionId,
    slotAvailability,
    showConflictModal,
    updateField,
    serviceDuration,
    setServiceDuration
  } = useCallStore()
  
  // Note: checkSlotAvailability removed - using direct API call in handleShowAlternatives
  
  const isHistorical = !!currentSessionId && !isCallActive
  const missingFields = getMissingFields(fields)
  
  const [stylists, setStylists] = useState<StylistInfo[]>([])
  const [priceInfo, setPriceInfo] = useState<PriceInfo | null>(null)
  const [_isLoadingPrice, setIsLoadingPrice] = useState(false)  // Prefixed unused var
  
  // Picker visibility states
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [showTimePicker, setShowTimePicker] = useState(false)
  const [showServiceSelector, setShowServiceSelector] = useState(false)
  
  // Parse services into array (supports comma-separated or single)
  const selectedServices = fields.service?.value 
    ? fields.service.value.split(',').map(s => s.trim()).filter(Boolean)
    : []
  
  useEffect(() => {
    fetch(`${API_URL}/api/stylists?active=1`)
      .then(res => res.json())
      .then(data => setStylists(data.stylists || []))
      .catch(err => console.error('Failed to load stylists:', err))
  }, [])
  
  useEffect(() => {
    const services = selectedServices
    const stylistName = fields.requestedStylist?.value
    
    if (services.length === 0) {
      setPriceInfo(null)
      return
    }
    
    const stylist = stylistName 
      ? stylists.find(s => s.name.toLowerCase() === stylistName.toLowerCase())
      : null
    
    setIsLoadingPrice(true)
    
    // Calculate total price for all selected services
    Promise.all(services.map(service => {
      const params = new URLSearchParams({ service })
      if (stylist) params.append('stylistId', stylist.id)
      return fetch(`${API_URL}/api/price?${params}`)
        .then(res => res.json())
        .then(data => {
          if (data.error) {
            console.warn(`Price lookup failed for "${service}":`, data.error)
            return { defaultPrice: 0, finalPrice: 0 }
          }
          return data
        })
    }))
      .then(results => {
        const totalDefault = results.reduce((sum, r) => sum + (r.defaultPrice || 0), 0)
        const totalFinal = results.reduce((sum, r) => sum + (r.finalPrice || 0), 0)
        // Sum up durations from all services
        const totalDuration = results.reduce((sum, r) => sum + (r.duration || 60), 0)
        
        setPriceInfo({
          defaultPrice: totalDefault,
          finalPrice: totalFinal,
          stylistId: stylist?.id || null,
          stylistName: stylist?.name || null
        })
        
        // Update service duration in store - critical for availability checks!
        // This ensures AI-extracted services get correct duration from DB
        if (totalDuration > 0) {
          console.log(`[Price fetch] Setting duration from API: ${totalDuration} mins for services: ${services.join(', ')}`)
          setServiceDuration(totalDuration)
        }
      })
      .catch(err => console.error('Failed to load price:', err))
      .finally(() => setIsLoadingPrice(false))
  }, [fields.service?.value, fields.requestedStylist?.value, stylists, setServiceDuration])
  
  const hasConflict = slotAvailability.isAvailable === false
  const isSlotAvailable = slotAvailability.isAvailable === true
  const isCheckingSlot = slotAvailability.isChecking
  // Allow booking if all essential fields are filled and no conflict, regardless of call state
  const hasAllEssentialFields = fields.firstName?.value && fields.phone?.value && 
    fields.service?.value && fields.date?.value && fields.time?.value
  const canBook = (isBookingReady || hasAllEssentialFields) && !hasConflict && !isCheckingSlot
  
  const selectedStylist = fields.requestedStylist?.value
    ? stylists.find(s => s.name.toLowerCase().includes(fields.requestedStylist?.value?.toLowerCase() || ''))
    : null

  const handleFieldUpdate = (fieldKey: string, value: string) => {
    updateField(fieldKey as keyof ProfileFields, {
      value,
      confidence: 100,
      status: 'confirmed',
      updatedAt: Date.now()
    })
  }

  const handleServicesChange = (services: string[], totalDuration: number) => {
    updateField('service', {
      value: services.join(', '),
      confidence: 100,
      status: 'confirmed',
      updatedAt: Date.now()
    })
    // Store duration in global store for availability checks and booking
    setServiceDuration(totalDuration || 60)
  }

  const handleDateChange = (date: string) => {
    updateField('date', {
      value: date,
      confidence: 100,
      status: 'confirmed',
      updatedAt: Date.now()
    })
  }

  const handleTimeChange = (time: string) => {
    updateField('time', {
      value: time,
      confidence: 100,
      status: 'confirmed',
      updatedAt: Date.now()
    })
  }

  const handleStylistSelect = (stylist: StylistInfo) => {
    updateField('requestedStylist', {
      value: stylist.name,
      confidence: 100,
      status: 'confirmed',
      updatedAt: Date.now()
    })
  }

  const handleShowAlternatives = async () => {
    // Get values directly from fields (not from store which might be stale)
    const dateValue = fields.date?.value
    const timeValue = fields.time?.value
    const stylistValue = fields.requestedStylist?.value
    const duration = serviceDuration || 60
    
    console.log('[handleShowAlternatives] Direct field values:', {
      date: dateValue,
      time: timeValue,
      stylist: stylistValue || 'any',
      duration
    })
    
    if (!dateValue || !timeValue) {
      console.log('[handleShowAlternatives] Missing date or time, aborting')
      return
    }
    
    // Make direct API call to avoid any state sync issues
    try {
      const API_URL = 'https://salonflow-api.empty-mud-1234.workers.dev'
      const response = await fetch(`${API_URL}/api/appointments/check-availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: dateValue,
          time: timeValue,
          duration,
          stylistName: stylistValue || undefined
        })
      })
      
      const result = await response.json()
      console.log('[handleShowAlternatives] API Result:', result)
      
      if (!result.available && result.conflicts?.length > 0) {
        showConflictModal({
          conflict: result.conflicts[0],
          suggestions: result.suggestions || []
        })
      } else if (result.available) {
        alert('This time slot is available! You can proceed with booking.')
      }
    } catch (error) {
      console.error('[handleShowAlternatives] API Error:', error)
    }
  }

  // Close pickers when clicking elsewhere handled by individual components
  
  // Service display value
  const serviceDisplayValue = selectedServices.length === 0 
    ? undefined 
    : selectedServices.length === 1 
      ? selectedServices[0]
      : `${selectedServices.length} services`

  return (
    <div className="h-full flex flex-col bg-white p-3 overflow-y-auto">
      {/* Header Row - Name & Contact */}
      <div className="flex gap-2 mb-2">
        <div className="flex-1 grid grid-cols-2 gap-1.5">
          <CompactField 
            label="First Name" 
            value={fields.firstName?.value}
            confidence={fields.firstName?.confidence}
            status={fields.firstName?.status}
            placeholder="First"
            fieldKey="firstName"
            onUpdate={handleFieldUpdate}
          />
          <CompactField 
            label="Last Name" 
            value={fields.lastName?.value}
            confidence={fields.lastName?.confidence}
            status={fields.lastName?.status}
            placeholder="Last"
            fieldKey="lastName"
            onUpdate={handleFieldUpdate}
          />
          <CompactField 
            label="Phone" 
            value={fields.phone?.value}
            confidence={fields.phone?.confidence}
            status={fields.phone?.status}
            placeholder="07xxx"
            fieldKey="phone"
            onUpdate={handleFieldUpdate}
          />
          <CompactField 
            label="Email" 
            value={fields.email?.value}
            confidence={fields.email?.confidence}
            status={fields.email?.status}
            placeholder="Optional"
            fieldKey="email"
            onUpdate={handleFieldUpdate}
          />
        </div>
        
        {/* Confidence Badge */}
        <div className="flex flex-col items-center justify-center">
          <div className={`
            w-11 h-11 rounded-full flex items-center justify-center text-base font-bold
            ${overallConfidence >= 80 ? 'bg-green-100 text-green-700' :
              overallConfidence >= 50 ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100 text-gray-500'}
          `}>
            {overallConfidence}%
          </div>
          <span className="text-[8px] text-gray-400 mt-0.5">Confidence</span>
        </div>
      </div>

      {/* Service & Date/Time Row - Interactive Pickers */}
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        <div className="relative">
          <PickerField 
            label="Service" 
            value={fields.service?.value}
            displayValue={serviceDisplayValue}
            confidence={fields.service?.confidence}
            status={fields.service?.status}
            placeholder="Select service"
            icon={Scissors}
            onClick={() => {
              setShowServiceSelector(!showServiceSelector)
              setShowDatePicker(false)
              setShowTimePicker(false)
            }}
            isOpen={showServiceSelector}
          />
          {showServiceSelector && (
            <ServiceSelector
              selectedServices={selectedServices}
              onChange={handleServicesChange}
              onClose={() => setShowServiceSelector(false)}
            />
          )}
        </div>
        
        <div className="relative">
          <PickerField 
            label="Date" 
            value={fields.date?.value}
            displayValue={formatDateDisplay(fields.date?.value)}
            confidence={fields.date?.confidence}
            status={fields.date?.status}
            placeholder="Select date"
            icon={Calendar}
            onClick={() => {
              setShowDatePicker(!showDatePicker)
              setShowServiceSelector(false)
              setShowTimePicker(false)
            }}
            isOpen={showDatePicker}
          />
          {showDatePicker && (
            <DatePicker
              value={fields.date?.value}
              onChange={handleDateChange}
              onClose={() => setShowDatePicker(false)}
            />
          )}
        </div>
        
        <div className="relative">
          <PickerField 
            label="Time" 
            value={fields.time?.value}
            displayValue={formatTimeDisplay(fields.time?.value)}
            confidence={fields.time?.confidence}
            status={fields.time?.status}
            placeholder="Select time"
            icon={Clock}
            onClick={() => {
              setShowTimePicker(!showTimePicker)
              setShowServiceSelector(false)
              setShowDatePicker(false)
            }}
            isOpen={showTimePicker}
          />
          {showTimePicker && (
            <TimePicker
              value={fields.time?.value}
              onChange={handleTimeChange}
              onClose={() => setShowTimePicker(false)}
            />
          )}
        </div>
      </div>

      {/* Selected Services Pills (when multiple) */}
      {selectedServices.length > 1 && (
        <div className="flex flex-wrap gap-1 mb-2 px-1">
          {selectedServices.map((service, i) => (
            <span 
              key={i}
              className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium border border-blue-200"
            >
              {service}
            </span>
          ))}
        </div>
      )}

      {/* Availability Status - Compact */}
      {fields.date?.value && fields.time?.value && (
        <div className={`
          px-2.5 py-1.5 rounded-lg mb-2 flex items-center gap-2 text-xs
          ${isCheckingSlot ? 'bg-gray-100 text-gray-600' :
            hasConflict ? 'bg-red-50 text-red-700 border border-red-200' : 
            isSlotAvailable ? 'bg-green-50 text-green-700 border border-green-200' :
            'bg-blue-50 text-blue-700 border border-blue-200'}
        `}>
          {isCheckingSlot ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Checking...</span>
            </>
          ) : hasConflict ? (
            <>
              <AlertTriangle className="h-3.5 w-3.5" />
              <span className="flex-1">Slot taken by {slotAvailability.conflict?.customerName}</span>
              <button onClick={handleShowAlternatives} className="text-[10px] underline font-medium">View alternatives</button>
            </>
          ) : isSlotAvailable ? (
            <>
              <CheckCircle className="h-3.5 w-3.5" />
              <span>Available</span>
            </>
          ) : (
            <>
              <Clock className="h-3.5 w-3.5" />
              <span className="flex-1">Check slot availability</span>
              <button onClick={handleShowAlternatives} className="text-[10px] underline font-medium">Check now</button>
            </>
          )}
        </div>
      )}

      {/* Stylist Selection - The Vault Style */}
      <div className="mb-2">
        <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1.5 block">
          Select Stylist
        </label>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {/* Any Team Member Option */}
          <button
            onClick={() => updateField('requestedStylist', { value: '', confidence: 100, status: 'confirmed', updatedAt: Date.now() })}
            className={`
              flex-shrink-0 w-14 flex flex-col items-center p-1.5 rounded-lg border-2 transition-all
              ${!selectedStylist ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
            `}
          >
            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center mb-0.5">
              <span className="text-gray-500 text-[9px] font-medium">Any</span>
            </div>
            <span className="text-[9px] text-gray-600 text-center leading-tight">Any team</span>
          </button>
          
          {/* Individual Stylists */}
          {stylists.map((stylist) => (
            <button
              key={stylist.id}
              onClick={() => handleStylistSelect(stylist)}
              className={`
                flex-shrink-0 w-14 flex flex-col items-center p-1.5 rounded-lg border-2 transition-all
                ${selectedStylist?.id === stylist.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
              `}
            >
              <div className="w-9 h-9 rounded-full overflow-hidden mb-0.5 ring-2 ring-white shadow">
                <img 
                  src={STYLIST_PHOTOS[stylist.name] || `https://ui-avatars.com/api/?name=${encodeURIComponent(stylist.name)}&background=${stylist.color_hex?.slice(1) || '888'}&color=fff&size=80`}
                  alt={stylist.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(stylist.name)}&background=${stylist.color_hex.slice(1)}&color=fff&size=80`
                  }}
                />
              </div>
              <span className="text-[9px] text-gray-700 font-medium text-center leading-tight truncate w-full">
                {stylist.name.split(' ')[0]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Price & Duration Display - Compact */}
      {priceInfo && (
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-lg px-3 py-2 mb-2">
          <div className="flex flex-col">
            <div>
              <span className="text-[10px] text-slate-400">Total</span>
              {selectedStylist && (
                <span className="text-[10px] text-slate-400 ml-1">w/ {selectedStylist.name.split(' ')[0]}</span>
              )}
              {selectedServices.length > 1 && (
                <span className="text-[10px] text-slate-400 ml-1">({selectedServices.length} services)</span>
              )}
            </div>
            {serviceDuration > 0 && (
              <div className="flex items-center gap-1 text-[10px] text-slate-400">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{serviceDuration} mins</span>
              </div>
            )}
          </div>
          <div className="flex items-baseline gap-2">
            {priceInfo.finalPrice !== priceInfo.defaultPrice && (
              <span className="text-xs text-slate-400 line-through">
                £{priceInfo.defaultPrice.toFixed(0)}
              </span>
            )}
            <span className="text-xl font-bold">
              £{priceInfo.finalPrice.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      {/* Notes Section with Save to Profile */}
      <NotesSection 
        value={fields.notes?.value}
        confidence={fields.notes?.confidence}
        status={fields.notes?.status}
        onUpdate={(value) => handleFieldUpdate('notes', value)}
        customerPhone={fields.phone?.value}
      />

      {/* Book Button - Always visible at bottom */}
      <div className="mt-4 pt-2 border-t border-gray-100 flex-shrink-0">
        {canBook ? (
          <button
            onClick={onBookAppointment}
            disabled={isBooking}
            className={`
              w-full py-2.5 px-4 rounded-lg font-semibold text-sm
              transition-all duration-200
              ${isBooking 
                ? 'bg-blue-400 text-white cursor-wait'
                : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98] shadow-lg'
              }
            `}
          >
            {isBooking ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Booking...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Calendar className="h-4 w-4" />
                Book Appointment
              </span>
            )}
          </button>
        ) : hasConflict ? (
          <button
            onClick={handleShowAlternatives}
            className="w-full py-2.5 px-4 rounded-lg font-semibold text-sm bg-red-100 text-red-700 hover:bg-red-200"
          >
            <span className="flex items-center justify-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              View Alternative Slots
            </span>
          </button>
        ) : (
          <button
            disabled
            className="w-full py-2.5 px-4 rounded-lg font-semibold text-sm bg-gray-100 text-gray-400"
          >
            {isCallActive ? (
              <span>Waiting: {missingFields.join(', ')}</span>
            ) : isHistorical ? (
              <span>{missingFields.length > 0 ? `Missing: ${missingFields.join(', ')}` : 'View only'}</span>
            ) : (
              <span>Start a call to begin</span>
            )}
          </button>
        )}
      </div>
    </div>
  )
}
